import { motion } from 'motion/react';
import { ArrowLeft } from 'lucide-react';
import { ChatTextAreaInternal as ChatTextArea } from '../../molecules/ChatTextArea';
import { useFileAttachments } from '../../../hooks/useFileAttachments';
import type { Step3Props } from '../types';
import { Button } from '@ideoz/ui';
import { IdeozLogo } from './IdeozLogo';
import { step3ContentMap } from '../config/step3Content';


export function Step3({ selectedWorkOption, onBack, facade, prototyperMode, onCompleteOnboarding, onCompleteAnalytics }: Step3Props & { prototyperMode?: boolean }): JSX.Element {
  // Get title and subtitle based on selected work option, with fallback to first option
  const content = selectedWorkOption
    ? step3ContentMap[selectedWorkOption]
    : step3ContentMap['ux-discovery'];

  const handleSubmit = async (text: string, attachmentIds: string[], llmFamily?: string) => {
    if (!facade) {
      console.error('Facade is undefined in Step3');
      return;
    }
    if (!text.trim() && attachmentIds.length === 0) return;
    
    onCompleteAnalytics?.();

    // Call API to update onboarding flag BEFORE navigating away
    // This ensures the flag is only set when user completes onboarding by submitting message
    if (onCompleteOnboarding) {
      try {
        await onCompleteOnboarding();
      } catch (error) {
        console.error('Failed to complete onboarding:', error);
        // Continue anyway - don't block the user
      }
    }
    
    facade.project.setCurrent('');
    facade.conversation.reset();
    facade.conversation.create({
      init_message: text,
      mode: prototyperMode ? 'prototyper' : 'uncover',
      attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
      llmFamily: llmFamily,
    });
    
    // Mark that prototyper was accessed from onboarding (for sidebar logic)
    if (prototyperMode && facade.conversation.setOnboardingPrototyper) {
      facade.conversation.setOnboardingPrototyper(true);
    }
    
    facade.navigation.toConversation();
  };

  // CTA for project creation if in prototyper mode and no project
  const showProjectCTA = prototyperMode && (!facade?.project?.currentProjectId || facade.project.currentProjectId === '');

  // Handler for closing chat (redirect to homepage)
  const handleCloseChat = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  return (
    <>
      <header className="flex flex-col gap-12">
        <div className="flex items-center gap-4">
          <motion.button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft size={20} className="text-gray-700" />
          </motion.button>
          <IdeozLogo />
        </div>
        <div className="flex flex-col gap-3 items-start justify-center ">
          <h2 className="text-primary-900 font-semibold text-3xl">{content.title}</h2>
          <p className="text-gray-900 text-xl">{content.subtitle}</p>
        </div>
      </header>
      <motion.div
        key="step3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1, ease: 'easeInOut' }}
        className="flex flex-col h-full"
      >
        {/* Chat Input */}
        <div className="mt-16">
          <ChatTextArea
            onSubmit={handleSubmit}
            placeholder={content.subtitle}
            data-testid="landing-chat-textarea"
            useFileAttachmentsHook={useFileAttachments}
            isWeb={true}
            initialLlmFamily={undefined}
          />
        </div>
        {/* {showProjectCTA && (
          <div className="mt-8 flex flex-col items-center">
            <p className="mb-4 text-gray-700 text-center">Create a project to save your progress and not lose your work.</p>
            <Button variant="primary" size="lg" onClick={() => facade?.project?.setDrawerVisibility?.(true)}>
              Create Project
            </Button>
            <Button variant="ghost" size="sm" className="mt-2" onClick={handleCloseChat}>
              Close Chat & Return Home
            </Button>
          </div>
        )} */}
      </motion.div>
    </>
  );
}
